import React from "react";
import { ExpressionValue } from "../../rule-engine-core/Expressions/ExpressionValue";
import {
    ExpressionValueType,
    ObjectExpressionValueType,
    StringExpressionValueType,
} from "../../rule-engine-core/Expressions/ExpressionValueType";
import styled from "styled-components";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";

import { CompactColor, ExpressionReactContext, ExpressionRenderType } from "../Structure/ExpressionReactContext";

import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";
import { ValidationError } from "../Utilities/ValidationError";
import { TestContext } from "../../CommonUI/Testing/TestContext";
import { BaseTestResultData } from "../../rule-engine-core/Common/Testing/BaseTestResultData";
import { ExpressionTestResultColorContext } from "../Testing/ExpressionTestResultColorContext";
import { Tooltip } from "@mui/material";
import { ObserveMultiState } from "../../CommonUI/StateManagement/ObserveMultiState";
import { RenderExpressionRule } from "../Utilities/RegisterExpressionRuleEditor";
import { ITestResultItem } from "../../rule-engine-core/Common/Testing/ITestResultItem";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

const SelectedElement = styled.span<{ renderType: ExpressionRenderType }>`
    display: flex;
    align-items: center;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            border: 1px solid  ${UIStyleConstants.BLUE_COLOR};
            box-sizing: border-box;
            border-radius: 3px;
            background-color ${UIStyleConstants.BLUE_LIGHT_COLOR};;
            margin-right: 5px;
            margin-left: 5px;
        `
            : `
            
            font-style: normal;
            font-weight: 500;
            font-size: 12px;
            line-height: 15px;
            color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
            white-space: pre;
        `}
`;

const SelectedExpression = styled.span<{ renderType: ExpressionRenderType }>`
    display: flex;
    align-items: center;

    position: relative;
    overflow: visible;
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                border: 1px solid  ${UIStyleConstants.BLUE_COLOR};
                box-sizing: border-box;
                border-radius: 3px;
                background-color ${UIStyleConstants.BLUE_LIGHT_COLOR};;
            
                margin-right: 0px;
                margin-left: 0px;
                padding-top: 3px;
                padding-bottom: 3px;

            `
            : `
                
                font-style: normal;
                font-weight: 500;
                font-size: 12px;
                line-height: 15px;
                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                white-space: pre;
        `}
`;

const ExpressionValueWrapper = styled.div<{ renderType: ExpressionRenderType }>`
    position: relative;
    overflow: visible;
    display: flex;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            padding-right: 4px;
            padding-left: 4px;
            flex: 1;
        `
            : ``}
`;

const ValueElement = styled.div<{ renderType: ExpressionRenderType; compactColor?: CompactColor }>`
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                border: 2px solid ${UIStyleConstants.WHITE_COLOR};
                border-radius: 4px;
                background-color:  ${UIStyleConstants.GRAY_LIGHT_COLOR};
                color:  ${UIStyleConstants.BLUE_COLOR};
                margin-right: 5px;
                margin-left: 5px;
                
                font-style: normal;
                font-weight: 600;
                font-size: 14px;
                line-height: 20px;
                transition: all .3s;
            
                &:hover {
                    border: 2px solid  ${UIStyleConstants.GRAY_LIGHTER_COLOR};
                    border-radius: 5px;
                    background-color: ${UIStyleConstants.WHITE_COLOR};
                }
            `
            : `
                
                font-style: normal;
                font-weight: 500;
                font-size: 12px;
                line-height: 15px;
                color: ${props.compactColor?.value || UIStyleConstants.GRAY_DARK_COLOR};
                white-space: pre;
            `}
    display: inline-flex;
    align-items: center;
    position: relative;
`;

export const VariableElement = styled.div<{ renderType: ExpressionRenderType; compactColor?: CompactColor }>`
    display: flex;

    ${(props) =>
        props.renderType === ExpressionRenderType.TEXT
            ? `
                flex-direction: column;
            `
            : `
                align-items: center;
            `}

    cursor: pointer;
    position: relative;

    > i {
        ${(props) =>
            props.renderType === ExpressionRenderType.FULL
                ? `
                    font-size: 14px;
                    line-height: 24px;
                    border-bottom: dotted 1px  ${UIStyleConstants.GRAY_COLOR}; 
                    display: block;
                    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                `
                : `
                    font-family: Source Code Pro;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 15px;
                    color: ${props.compactColor?.variable || "" + UIStyleConstants.GRAY_COLOR};
                    white-space: pre;
                `}
    }

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            padding: 3px 0;
            margin-right: 4px;
            margin-left: 4px;
            &:hover {
                > i {
                    border-bottom: solid 2px  ${UIStyleConstants.GRAY_COLOR}; 
                }
            }
        `
            : ``}
`;

const InnerValueDiv = styled.div<{ renderType: ExpressionRenderType }>`
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            padding: 4px 8px;
            display: flex;
        `
            : `
            padding: 0 4px;
        `}
`;

const SubExpressionElement = styled.div<{ isText: boolean }>`
    position: relative;
    display: flex;
    flex: 1;

    ${(props) =>
        props.isText
            ? `
                flex-direction: column;
            `
            : ``}
`;

const TestValueRender = styled.div<{ type: ExpressionValueType; color?: string }>`
    text-align: ${(props) => (props.type === ExpressionValueType.SUBEXPRESSION ? "right" : "left")};
    font-size: 8px;

    line-height: 1;

    white-space: pre;

    ${(props) => (props.color ? `color: ${props.color};` : ``)}
`;

const FullModeTestValueRender = styled.div<{ type: ExpressionValueType; color?: string }>`
    position: absolute;

    right: ${(props) => (props.type === ExpressionValueType.SUBEXPRESSION ? "10px" : "0")};
    bottom: -12px;
    font-size: 12px;

    line-height: 1;

    white-space: pre;

    ${(props) => (props.color ? `color: ${props.color};` : ``)}
`;

const TestErrorValueRender = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    border: solid 1px ${UIStyleConstants.RED_COLOR};
    border-radius: 6px;
`;

const MissingElement = styled.span<{ renderType: ExpressionRenderType }>`
    display: flex;
    align-items: center;
    box-sizing: border-box;
    border-radius: 3px;
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            margin-right: 5px;
            margin-left: 5px;
        `
            : `white-space: pre;`}
`;

const MissingTextDiv = styled("div")<{
    renderType: ExpressionRenderType;
    compactColor?: CompactColor;
}>`
    letter-spacing: 0.05em;
    background: ${UIStyleConstants.WHITE_COLOR};
    box-sizing: border-box;
    border-radius: 4px;
    transition: all 0.3s;
    position: relative;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            color: ${UIStyleConstants.BLUE_COLOR};
            padding: 4px 12px;
            
            font-style: normal;
            font-weight: bold;
            font-size: 14px;
            line-height: 24px;
            border: 1px solid ${UIStyleConstants.BLUE_COLOR};
            cursor: pointer;

            &:hover {
                background: ${UIStyleConstants.BLUE_COLOR};
                border-color: ${UIStyleConstants.WHITE_COLOR};
                color: ${UIStyleConstants.WHITE_COLOR};
            }
        `
            : `
            
            font-size: 12px;
            font-style: italic;
            font-weight: 400;
            line-height: 15px;
            letter-spacing: 0px;
            text-align: left;
            color: ${props.compactColor?.missing || UIStyleConstants.GRAY_DARK_COLOR};
            white-space: pre;
        `}
`;

const TextDiv = styled("div")<{ renderType: ExpressionRenderType }>`
    color: ${UIStyleConstants.BLUE_COLOR};

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                padding: 4px 8px;
                
                font-style: normal;
                font-weight: 600;
                font-size: 14px;
                line-height: 20px;
            `
            : `
                white-space: pre;
                
                font-size: 12px;
                font-weight: 400;
                line-height: 15px;
                letter-spacing: 0px;
                text-align: left;
                font-style: normal;
                color:  ${UIStyleConstants.GRAY_LIGHT_COLOR};
                white-space: pre;

            `}
`;

export class ExpressionValueRenderer extends React.Component<{ el: ExpressionValue }> {
    static contextType = ExpressionReactContext;

    context!: React.ContextType<typeof ExpressionReactContext>;

    @AutoBind
    handleSelectExpression(event: React.MouseEvent<HTMLElement>) {
        if (this.context.renderType === ExpressionRenderType.FULL) {
            event.stopPropagation();
            this.props.el.editContext?.setSelection(this.props.el._id);
        }
    }

    checkPassedInTest(testcontext: BaseTestResultData | undefined): boolean | null {
        if (!testcontext) return null;
        let testitem = testcontext.findItem(this.props.el._id);
        if (!testitem) return null;

        if (testitem.result === "true") return true;

        if (testitem.result === "false") return false;

        return null;
    }

    getTestValueColor(testcontext: BaseTestResultData | undefined): string | undefined {
        let result = this.checkPassedInTest(testcontext);

        if (result === null) return undefined;

        let color: string | undefined = result ? UIStyleConstants.GREEN_COLOR : UIStyleConstants.RED_COLOR;

        return color;
    }

    render() {
        return (
            <TestContext.Consumer>
                {(testcontext: BaseTestResultData | undefined) => {
                    return (
                        <ExpressionValueWrapper
                            renderType={this.context.renderType}
                            id={this.props.el._id}
                        >
                            <ObserveMultiState
                                listenTo={[this.props.el, this.props.el.editContext!]}
                                control={() => {
                                    let selected = false;

                                    if (this.props.el.editContext?.getSelection()) {
                                        selected = this.props.el.editContext?.getSelection() === this.props.el._id;
                                    }
                                    return (
                                        <>
                                            {
                                                // no need to render for selected elements when compact version
                                                selected
                                                    ? this.renderSelectedElements(testcontext)
                                                    : this.renderUnselectedElements(testcontext)
                                            }
                                        </>
                                    );
                                }}
                            />
                        </ExpressionValueWrapper>
                    );
                }}
            </TestContext.Consumer>
        );
    }

    @AutoBind
    renderMissingElement() {
        let text = this.props.el.editContext?.emptyExpressionText ?? this.context.missing.default;
        switch (this.props.el.valueType) {
            case ExpressionValueType.SUBEXPRESSION:
                text = this.context.missing.subexpression;
                break;
            case ExpressionValueType.VARIABLE:
                text = this.context.missing.variable;
                break;
            case ExpressionValueType.VALUE:
                text = this.context.missing.value;
                break;
        }

        return (
            <MissingTextDiv renderType={this.context.renderType}>
                {text}
                {this.renderError()}
            </MissingTextDiv>
        );
    }

    @AutoBind
    renderError() {
        return <ValidationError value={this.props.el} />;
    }

    @AutoBind
    renderVariableName() {
        return this.props.el.variableName;
    }

    @AutoBind
    renderValue() {
        if (this.props.el.expectedType.list) {
            if (Array.isArray(this.props.el.value)) {
                return this.props.el.value.join(",");
            }
        } else if (
            this.props.el.expectedType.type === DataTypes.OBJECT &&
            this.props.el.objectValueType === ObjectExpressionValueType.MAPPED
        ) {
            return "[Mapped Data]";
        } else if (
            this.props.el.expectedType.type === DataTypes.STRING &&
            this.props.el.stringValueType === StringExpressionValueType.MERGE
        ) {
            return "[Merged Text]";
        } else if (this.props.el.expectedType.type === DataTypes.BOOLEAN) {
            return this.props.el.value ? "True" : "False";
        } else if (this.props.el.value && typeof this.props.el.value === "string" && this.props.el.value.length > 20) {
            return this.props.el.value.substring(0, 20) + "... [+" + (this.props.el.value.length - 20) + "]";
        } else {
            return this.props.el.value;
        }
    }

    renderTestValue(textcontext: BaseTestResultData | undefined) {
        if (!textcontext) return null;

        let testitem: ITestResultItem | undefined;

        let itemid: string = this.props.el._id;

        testitem = textcontext.findItem(itemid);

        if (!testitem) {
            return null;
        }

        if (!testitem.isError) {
            return (
                <ExpressionTestResultColorContext.Consumer>
                    {(testcolor) => (
                        <>
                            {this.context.renderType === ExpressionRenderType.TEXT ? (
                                <TestValueRender
                                    type={this.props.el.valueType}
                                    color={testcolor}
                                >
                                    {testitem?.result}
                                </TestValueRender>
                            ) : (
                                <FullModeTestValueRender
                                    type={this.props.el.valueType}
                                    color={testcolor}
                                >
                                    {testitem?.result}
                                </FullModeTestValueRender>
                            )}
                        </>
                    )}
                </ExpressionTestResultColorContext.Consumer>
            );
        }

        if (this.props.el._id === this.props.el.editContext?.definition?.expressionRoot?._id) return null;

        return (
            <Tooltip title={testitem.result!}>
                <TestErrorValueRender />
            </Tooltip>
        );
    }

    @AutoBind
    renderSelectedElements(testcontext: BaseTestResultData | undefined) {
        if (this.props.el.valueType === ExpressionValueType.VARIABLE) {
            return (
                <SelectedElement
                    renderType={this.context.renderType}
                    onClick={testcontext ? undefined : this.handleSelectExpression}
                >
                    {this.props.el.variableName ? (
                        <TextDiv renderType={this.context.renderType}>
                            {this.renderVariableName()}
                            {this.renderError()}
                            {this.renderTestValue(testcontext)}
                        </TextDiv>
                    ) : (
                        this.renderMissingElement()
                    )}
                </SelectedElement>
            );
        } else if (this.props.el.valueType === ExpressionValueType.VALUE) {
            return (
                <SelectedElement
                    renderType={this.context.renderType}
                    onClick={testcontext ? undefined : this.handleSelectExpression}
                >
                    {this.props.el.value !== null && this.props.el.value !== undefined ? (
                        <TextDiv renderType={this.context.renderType}>
                            {this.renderValue()} {this.renderError()}
                        </TextDiv>
                    ) : (
                        this.renderMissingElement()
                    )}
                </SelectedElement>
            );
        } else if (this.props.el.valueType === ExpressionValueType.SUBEXPRESSION) {
            if (!RenderExpressionRule(this.props.el.subExpression)) {
                return (
                    <SelectedElement
                        renderType={this.context.renderType}
                        onClick={testcontext ? undefined : this.handleSelectExpression}
                    >
                        {this.renderMissingElement()}
                    </SelectedElement>
                );
            } else {
                return (
                    <SelectedExpression
                        renderType={this.context.renderType}
                        onClick={testcontext ? undefined : this.handleSelectExpression}
                    >
                        {RenderExpressionRule(this.props.el.subExpression) ?? this.renderMissingElement()}
                        {this.renderTestValue(testcontext)}

                        {this.renderError()}
                    </SelectedExpression>
                );
            }
        } else {
            return (
                <SelectedElement
                    renderType={this.context.renderType}
                    onClick={testcontext ? undefined : this.handleSelectExpression}
                >
                    {this.renderMissingElement()}
                </SelectedElement>
            );
        }
    }

    @AutoBind
    renderUnselectedElements(testcontext: BaseTestResultData | undefined) {
        if (this.props.el.valueType === ExpressionValueType.VARIABLE) {
            return (
                <VariableElement
                    onClick={testcontext ? undefined : this.handleSelectExpression}
                    renderType={this.context.renderType}
                    compactColor={this.context.color}
                >
                    <i>{this.props.el.variableName ? this.renderVariableName() : this.renderMissingElement()}</i>
                    {this.renderError()}
                    {this.renderTestValue(testcontext)}
                </VariableElement>
            );
        } else if (this.props.el.valueType === ExpressionValueType.VALUE) {
            return (
                <ValueElement
                    renderType={this.context.renderType}
                    onClick={testcontext ? undefined : this.handleSelectExpression}
                >
                    {this.props.el.value !== undefined && this.props.el.value !== null ? (
                        <>
                            <InnerValueDiv renderType={this.context.renderType}>{this.renderValue()}</InnerValueDiv>
                            {this.renderError()}
                            {/* {this.renderTestValue(testcontext)} */}
                        </>
                    ) : (
                        this.renderMissingElement()
                    )}
                </ValueElement>
            );
        } else if (this.props.el.valueType === ExpressionValueType.SUBEXPRESSION) {
            return (
                <SubExpressionElement
                    onClick={testcontext ? undefined : this.handleSelectExpression}
                    isText={this.context.renderType === ExpressionRenderType.TEXT}
                >
                    {RenderExpressionRule(this.props.el.subExpression) ?? this.renderMissingElement()}{" "}
                    {this.renderError()}
                    {this.renderTestValue(testcontext)}
                </SubExpressionElement>
            );
        } else {
            // FOR: this.props.el.valueType === ExpressionValueType.UNSET
            return (
                <MissingElement
                    onClick={testcontext ? undefined : this.handleSelectExpression}
                    renderType={this.context.renderType}
                >
                    <>{this.renderMissingElement()}</>
                </MissingElement>
            );
        }
    }
}
